import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {alpha} from "@mui/material/styles";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {getLocalizedValue} from "./utils";

interface Props {
    messages: Record<string, string>[] | undefined;
    locale: string;
    color: string;
    borderOpacity: number;
    printMode?: boolean;
}

export function RecommendationMessagesElement({
    messages,
    locale,
    color,
    borderOpacity,
    printMode = false,
}: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const recommendations = useMemo(() => messages
        ?.map(message => getLocalizedValue(message, locale))
        .filter((message): message is string => !!message) ?? [], [locale, messages]);

    useEffect(() => {
        const content = contentRef.current;
        if (!content || printMode || isExpanded) return;

        const checkTruncation = () => {
            const textElements = content.querySelectorAll<HTMLElement>("[data-recommendation-text]");
            setIsTruncated(Array.from(textElements).some(
                element => element.scrollWidth > element.clientWidth
            ));
        };

        checkTruncation();
        const resizeObserver = typeof ResizeObserver === "undefined"
            ? undefined
            : new ResizeObserver(checkTruncation);
        resizeObserver?.observe(content);
        window.addEventListener("resize", checkTruncation);
        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", checkTruncation);
        };
    }, [isExpanded, printMode, recommendations]);

    if (!recommendations.length) return null;

    const showFull = isExpanded || printMode;
    const expandable = isTruncated || isExpanded;
    const clickable = !printMode && expandable;
    const toggleExpanded = () => setIsExpanded(current => !current);
    const border = `1px solid ${alpha(color, borderOpacity)}`;

    const content = (
        <Box
            ref={contentRef}
            display="flex"
            flexDirection="column"
            gap={0.5}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-expanded={clickable ? isExpanded : undefined}
            onClick={clickable ? toggleExpanded : undefined}
            onKeyDown={event => {
                if (!clickable || (event.key !== "Enter" && event.key !== " ")) return;
                event.preventDefault();
                event.stopPropagation();
                toggleExpanded();
            }}
            sx={{
                width: "100%",
                minWidth: 0,
                cursor: clickable ? "pointer" : "inherit",
                userSelect: clickable ? "none" : "auto",
                pointerEvents: printMode ? "none" : "auto",
                "&:hover": clickable ? {opacity: 0.85} : undefined,
                "@media print": {
                    cursor: "default !important",
                    pointerEvents: "none !important",
                    opacity: "1 !important",
                },
            }}
        >
            {recommendations.map((message, index) => (
                <Box
                    component="span"
                    key={index}
                    sx={{
                        display: "inline-flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        minHeight: 28,
                        width: "100%",
                        minWidth: 0,
                        px: 1,
                        py: 0.5,
                        borderRadius: "6px",
                        border,
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        boxShadow: "0 1px 1px rgba(15, 23, 42, 0.03)",
                        "@media print": {
                            px: "6px !important",
                            py: "3px !important",
                            minHeight: "20px !important",
                            border: `1px solid ${alpha(color, 0.15)} !important`,
                            boxShadow: "none !important",
                            backgroundColor: "#fff !important",
                        },
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            width: 8,
                            height: 8,
                            mt: "5px",
                            borderRadius: "50%",
                            backgroundColor: color,
                            flex: "0 0 auto",
                            "@media print": {
                                width: "6px !important",
                                height: "6px !important",
                                mt: "4px !important",
                            },
                        }}
                    />
                    <Box
                        component="span"
                        data-recommendation-text
                        sx={{
                            mt: "2px",
                            minWidth: 0,
                            lineHeight: "16px",
                            whiteSpace: showFull ? "normal" : "nowrap",
                            overflow: showFull ? "visible" : "hidden",
                            textOverflow: showFull ? "clip" : "ellipsis",
                            width: "100%",
                            "@media print": {
                                fontSize: "11px !important",
                                lineHeight: "14px !important",
                                whiteSpace: "normal !important",
                                overflow: "visible !important",
                                textOverflow: "clip !important",
                                mt: "0px !important",
                            },
                        }}
                    >
                        {message}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    if (!clickable) return content;

    return (
        <Tooltip title={isExpanded ? "Click to collapse" : "Click to expand"} arrow>
            {content}
        </Tooltip>
    );
}
